/**
 * Retry Storage Utility
 * Manages sessionStorage for retry operations with query parameters
 */

const RETRY_PREFIX = 'retry-'
const RETRY_EXPIRATION_MS = 3600000 // 1 hour

/**
 * Store retry data in sessionStorage and return a unique key
 * @param {Object} data - The data to store
 * @returns {string} - The unique key for retrieval
 */
export function setRetryData(data) {
  try {
    const timestamp = Date.now()
    const key = `${RETRY_PREFIX}${timestamp}`
    
    const storageItem = {
      data: data,
      timestamp: timestamp,
      expires: timestamp + RETRY_EXPIRATION_MS
    }
    
    sessionStorage.setItem(key, JSON.stringify(storageItem))
    console.log('Retry data stored with key:', key)
    
    return key
  } catch (error) {
    console.error('Error storing retry data:', error)
    // Return null to fallback to direct query params
    return null
  }
}

/**
 * Retrieve retry data from sessionStorage and delete it
 * @param {string} key - The key to retrieve
 * @returns {Object|null} - The stored data or null if not found/expired
 */
export function getRetryData(key) {
  try {
    if (!key || !key.startsWith(RETRY_PREFIX)) {
      return null
    }
    
    const stored = sessionStorage.getItem(key)
    if (!stored) {
      console.warn('Retry data not found for key:', key)
      return null
    }
    
    const storageItem = JSON.parse(stored)
    
    // Check expiration
    if (Date.now() > storageItem.expires) {
      console.warn('Retry data expired for key:', key)
      sessionStorage.removeItem(key)
      return null
    }
    
    // Delete after successful retrieval
    sessionStorage.removeItem(key)
    console.log('Retry data retrieved and deleted for key:', key)
    
    return storageItem.data
  } catch (error) {
    console.error('Error retrieving retry data:', error)
    // Try to clean up the key anyway
    try {
      sessionStorage.removeItem(key)
    } catch (e) {
      // Ignore cleanup errors
    }
    return null
  }
}

/**
 * Clear all retry data from sessionStorage
 * Should be called on login/logout
 */
export function clearAllRetryData() {
  try {
    const keys = []
    
    // Collect all retry keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(RETRY_PREFIX)) {
        keys.push(key)
      }
    }
    
    // Remove all retry keys
    keys.forEach(key => {
      sessionStorage.removeItem(key)
    })
    
    if (keys.length > 0) {
      console.log(`Cleared ${keys.length} retry data entries`)
    }
  } catch (error) {
    console.error('Error clearing retry data:', error)
  }
}

/**
 * Clean up expired retry data
 * Optional: Can be called periodically or on page load
 */
export function cleanExpiredRetryData() {
  try {
    const now = Date.now()
    const keys = []
    
    // Collect all retry keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(RETRY_PREFIX)) {
        keys.push(key)
      }
    }
    
    // Check and remove expired entries
    let removed = 0
    keys.forEach(key => {
      try {
        const stored = sessionStorage.getItem(key)
        if (stored) {
          const storageItem = JSON.parse(stored)
          if (now > storageItem.expires) {
            sessionStorage.removeItem(key)
            removed++
          }
        }
      } catch (e) {
        // If parse fails, remove the corrupted entry
        sessionStorage.removeItem(key)
        removed++
      }
    })
    
    if (removed > 0) {
      console.log(`Cleaned up ${removed} expired retry data entries`)
    }
  } catch (error) {
    console.error('Error cleaning expired retry data:', error)
  }
}

